/**
 * What a clearance run says about each station bin in 3D — the verdict, the
 * colour it resolves to — plus the pure half of the source selector the scene
 * mounts on.
 *
 * There are no plate/corridor adapters here any more: the flat corridor plates
 * were drawn at the alignment datum, which for a profile based at height 0 is
 * the same plane as the swept envelope's floor, so the two objects sat in one
 * plane saying different things. The envelope carries the verdict now
 * (`buildBandedEnvelope`), and it takes its colours from this module.
 *
 * Pure and DOM-free, like its station-volumes twin: the verdict per bin and the
 * colour ladder are the parts that can be wrong in a way a screenshot will not
 * reveal, so they are unit-tested here and the r3f component is left with
 * nothing but wiring.
 */
import type { StationDockDataset } from '@/atoms/stationVolumes.atom';
import { pointAtStation, type Alignment, type ClipDims } from '@/core/alignment';
import { binCentreStation } from '@/lib/stationVolumes/aggregate';
import type { SvPalette } from '@/lib/stationVolumes/tokens';

import { CLEARANCE_K2_KEY, k2Verdict, type ClearanceBarKind } from './chart';
import type {
  ClearanceBin,
  ClearanceLayerMeta,
  ClearanceRunParams,
  ClearanceSummary,
  DerivedClearanceLayerMetadata,
} from './types';

/**
 * The K2 ladder in the scene: `k2Verdict`'s three rungs — the SAME predicate the
 * chart's bars escalate on (ruling P14), imported rather than restated — plus
 * one rung that exists only in 3D.
 *
 * `empty` is layered on top, not folded in: a bin the sweep never scored must
 * not be drawn as "clear" (principle P5 — a span of the envelope is a claim
 * about ground, a bar is not). It is the one thing the two renderers
 * legitimately disagree about, because the chart has no way to say it.
 */
export type ClearanceBandState = ClearanceBarKind | 'empty';

export function clearanceBandState(bin: ClearanceBin): ClearanceBandState {
  return bin.pointCount === 0 ? 'empty' : k2Verdict(bin);
}

/**
 * Which palette field paints which verdict. The three scored rungs come from
 * `CLEARANCE_K2_KEY`, the same map the chart's bars and both legends draw
 * from, so the envelope, the bars and the key cannot drift and a light/dark
 * flip repaints all three together.
 *
 * `empty` is the pale `zero` neutral rather than the `clear` grey: the sweep
 * never scored that ground, and a bin that says "clear" about ground nobody
 * measured is the one lie this instrument must not tell. Pale reads as "not
 * the same thing as the clear span next door" without claiming a verdict.
 */
const BAND_HUE: Record<ClearanceBandState, keyof SvPalette> = { ...CLEARANCE_K2_KEY, empty: 'zero' };

/**
 * The verdict colour for one bin, as a CSS hex — what `EnvelopeBand.color`
 * wants.
 *
 * Categorical, not a ramp: a violation is not "more" of a near-miss, it is a
 * different verdict, and a diverging ramp over a count would invent an ordering
 * the data does not have (the same argument `bandColor`'s docstring makes
 * against RAINBOW).
 */
export function clearanceBandColor(bin: ClearanceBin, palette: SvPalette): string {
  return palette[BAND_HUE[clearanceBandState(bin)]];
}

/** The least a layer has to look like for the bands to read its run off it. */
interface LayerLike {
  id: string;
  metadata?: unknown;
}

/** What an open dock has to carry for the bands to be about it. */
interface DockLike {
  layerId: string;
  dataset: StationDockDataset;
}

export interface ClearanceBandSource<D extends DockLike> {
  /** The open dock, or null when nothing clearance-shaped is on screen. */
  dock: D | null;
  meta: ClearanceLayerMeta | null;
  batchId: string | undefined;
}

/**
 * Gate, then DATASET, then the layer's metadata — in that order, in one step.
 *
 * The gate is applied to `dock` **first**, before anything downstream reads it:
 * the visibility atom defaults to `true`, so without this a gated user would
 * get bands on the very first open — and would pull the (multi-megabyte) bins
 * JSON down to draw them. Collapsing `dock` collapses `meta` and the fetch key
 * with it, so a locked user renders nothing AND fetches nothing.
 *
 * The dataset check is what keeps the clearance envelope from painting
 * underneath an open VOLUMES dock (ruling R2's shared dock atom, and the
 * mirror of `useBandSource`'s own guard): only one corridor is ever mounted.
 *
 * Pure so all of that is assertable without a GPU; `useClearanceRunSource`
 * (`components/3D/useClearanceRunSource.ts`) is this function plus the atoms
 * and the fetch.
 */
export function clearanceBandSource<D extends DockLike>(
  gateAllowed: boolean,
  openDock: D | null,
  layers: readonly LayerLike[],
): ClearanceBandSource<D> {
  const dock = gateAllowed && openDock?.dataset === 'clearance' ? openDock : null;
  const layer = dock ? layers.find((l) => l.id === dock.layerId) : undefined;
  const layerMeta = (layer?.metadata ?? null) as DerivedClearanceLayerMetadata | null;
  return { dock, meta: layerMeta?.clearance ?? null, batchId: layerMeta?.batchId };
}

/** Breathing room around the profile's own bounding box, per side (m). */
const CLIP_PAD_M = 2;
/** Never hand the clip box a degenerate slab — mirrors `clipDimsForRun`. */
const CLIP_MIN_LENGTH_M = 1;
const CLIP_MIN_SPAN_M = 2;

/** To the centimetre: these land in the walk toolbar's clip-box writes, where
 *  `2.3500000000000005` is noise and a centimetre is all the slab needs. */
const r2 = (v: number) => Math.round(v * 100) / 100;

/**
 * The cross-section slab for "clip to station" on a clearance run.
 *
 * `clipDimsForRun` cannot be reused: it sizes the slab from the
 * `station_offsets.f32` zTop/zBottom planes, which a clearance run does not
 * produce (ruling R1). The profile's own bounding box is the honest
 * equivalent — it is exactly the volume the check was made over — padded so the
 * structure the points belong to stays visible around it.
 *
 * `verticalCentre` is an ABSOLUTE ELEVATION (ruling P7): `clipBoxAtStation`
 * reads it as the box centre's z in the scene's own frame, defaulting to the
 * alignment's z at the station. So it is built from that z, plus the offset the
 * profile is hung at, plus the profile's own mid-height — never the relative
 * `verticalOffsetM + midHeight` on its own, which would park a 5 m slab a few
 * metres above the origin and clip the entire cloud away with nothing on
 * screen to explain it (the ruling-D35 failure the `ClipDims` docstring warns
 * about).
 *
 * That is also why this takes the alignment and a station rather than
 * `params` alone: on a graded alignment the elevation the slab belongs at
 * moves with the station, so the caller recomputes as the walk steps and the
 * slab follows the grade instead of sliding out of the cloud.
 */
export function clearanceClipDims(params: ClearanceRunParams, alignment: Alignment, station: number): ClipDims {
  const width = params.profileMaxOffsetM - params.profileMinOffsetM;
  const height = params.profileMaxHeightM - params.profileMinHeightM;
  const z = pointAtStation(alignment, station).z;
  return {
    length: r2(Math.max(CLIP_MIN_LENGTH_M, params.stationIntervalM)),
    width: r2(Math.max(CLIP_MIN_SPAN_M, width + 2 * CLIP_PAD_M)),
    height: r2(Math.max(CLIP_MIN_SPAN_M, height + 2 * CLIP_PAD_M)),
    // Positive = right. A profile entirely right of the centreline has to take
    // the slab with it, or the box cuts in half the thing it exists to show.
    lateralOffset: r2((params.profileMinOffsetM + params.profileMaxOffsetM) / 2),
    // The profile hangs off the centreline at `verticalOffsetM`, so the slab is
    // centred on the profile's mid-height — measured from the alignment's own
    // elevation at this station, which is what makes the figure absolute.
    verticalCentre: r2(z + params.verticalOffsetM + (params.profileMaxHeightM + params.profileMinHeightM) / 2),
  };
}

/**
 * Where "Go to worst" walks to: the run's worst bin, and the station to land on.
 *
 * The worker recorded the EXACT station of the deepest intrusion, so that is
 * the station used rather than the bin's midpoint — the difference is the
 * difference between landing ON the point the reader asked to see and landing
 * somewhere in the twenty metres around it. The midpoint is the fallback for a
 * run whose summary predates that field.
 *
 * Returns null on a clean run, and on a summary whose `worstBinIndex` does not
 * name a bin the JSON carries (a truncated or refetched bins array must not
 * take the dock down).
 */
export function clearanceWorstTarget(json: {
  summary: Pick<ClearanceSummary, 'worstBinIndex' | 'worstStationInternalM'>;
  bins: readonly ClearanceBin[];
}): { bin: ClearanceBin; station: number } | null {
  const i = json.summary.worstBinIndex;
  const bin = i === null || i === undefined ? undefined : json.bins[i];
  if (!bin) return null;
  const exact = json.summary.worstStationInternalM;
  return { bin, station: exact !== null && Number.isFinite(exact) ? exact : binCentreStation(bin) };
}
